import { Component, computed, forwardRef, inject, Injector, model, OnInit, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, NgForm, Validator } from '@angular/forms';
import { TransportEndpointOwner } from 'src/app/partnerportal/components/select-transport-dialog/select-transport-dialog.component';
import { CredentialsStatus, Transport, TransportType } from 'src/app/partnerportal/models/partner-onboarding';
import { GenericModalService, ModalSettingsTemplate } from 'src/app/shared/generic-modal/generic-modal.service';
import { SftpTransportEditorComponent, SftpTransportEditorDialogResult, SftpTransportResult } from '../sftp-transport-editor/sftp-transport-editor.component';
import { GroupBoxComponent } from '../../../group-box/group-box.component';

@Component({
  selector: 'app-sftp-transport-viewer',
  standalone: true,
  imports: [FormsModule, GroupBoxComponent],
  templateUrl: './sftp-transport-viewer.component.html',
  styleUrl: './sftp-transport-viewer.component.scss',
  providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => SftpTransportViewerComponent),
        multi: true
      },
      {
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => SftpTransportViewerComponent),
        multi: true
      }
    ],
})
export class SftpTransportViewerComponent implements OnInit, Validator, ControlValueAccessor {
  modalService = inject(GenericModalService);
  form = viewChild<NgForm>('form');
  value = model<Transport>();
  disabled = signal<boolean>(false);
  injector = inject(Injector);
  ngControl: NgControl | null = null;

  ngOnInit(): void {
    try {
      this.ngControl = this.injector.get(NgControl);
    }
    catch {
      // No NgControl
    }
  }

  get control(): FormControl {
    return this.ngControl?.control as FormControl;
  }

  get isValid(): boolean {
    return !this.control || this.control.invalid !== true;
  }

  endpointOwner = computed(() => {
    switch (this.value()?.Type) {
      case TransportType.SFTPCustomerEndpoint:
      case TransportType.IntegratedSFTP:
        return TransportEndpointOwner.Customer;
      case TransportType.SFTPPartnerEndpoint:
        return TransportEndpointOwner.Partner;
      default:
        return null;
    }
  });

  endpointOwnerNote = computed(() => {
    const owner = this.endpointOwner();
    switch (owner) {
      case TransportEndpointOwner.Customer:
        return 'Your partner will use the following connection details to connect to your system';
      case TransportEndpointOwner.Partner:
        return 'Your partner will be asked to provide the following connection details';
      default:
        return '';
    }
  });

  sftpTypeText = computed(() => {
    switch (this.value()?.Type) {
      case TransportType.SFTPCustomerEndpoint:
        return 'Your own SFTP server';
      case TransportType.SFTPPartnerEndpoint:
        return 'Partner SFTP Server';
      case TransportType.IntegratedSFTP:
        return 'Integrated SFTP';
      default:
        return '';
    }
  });

  inboundFolder = computed(() => {
    const field = this.value()?.TransportFields?.find(f => f.Name === 'InFolder');
    const inboundFolder = field?.Value;
    return inboundFolder;
  });

  outboundFolder = computed(() => {
    const field = this.value()?.TransportFields?.find(f => f.Name === 'OutFolder');
    const outboundFolder = field?.Value;
    return outboundFolder;
  });

  hostname = computed(() => {
    const field = this.value()?.TransportFields?.find(f => f.Name === 'Hostname');
    const hostname = field?.Value;
    return hostname;
  });

  port = computed(() => {
    const field = this.value()?.TransportFields?.find(f => f.Name === 'Port');
    const port = field?.Value;
    return port;
  });

  credentialsStatusText = computed(() => {
    switch (this.value()?.CredentialsStatus) {
      case CredentialsStatus.NotProvided:
        return 'Credentials not provided - to be requested by partner';
      case CredentialsStatus.Provided:
        return 'Credentials provided';
      case CredentialsStatus.Requested:
        return 'Credentials requested by partner';
      default:
        return '';
    }
  });

  async editTransport(transport: Transport): Promise<void> {
    const modalSettings = new ModalSettingsTemplate('lg', 'static');
    const result = await this.modalService.openComponentWithInitialize<SftpTransportResult, SftpTransportEditorComponent>(SftpTransportEditorComponent, modalSettings, (dialog) => {
      dialog.transport = transport;
    });

    if (result?.dialogResult === SftpTransportEditorDialogResult.Cancel) {
      return;
    }
    this.value.set(result.transport);
    this.onChange(result.transport);
  }

  validateTransport() {
    switch (this.value()?.Type) {
      case TransportType.SFTPCustomerEndpoint:
        if (!this.value()?.TransportFields?.find(f => f.Name === 'Hostname' && f.Value) ||
            !this.value()?.TransportFields?.find(f => f.Name === 'InFolder' && f.Value) ||
            !this.value()?.TransportFields?.find(f => f.Name === 'OutFolder' && f.Value)) {
          return ['Invalid'];
        }
        break;
      case TransportType.SFTPPartnerEndpoint:
        return [];
      case TransportType.IntegratedSFTP:
        if (!this.value()?.TransportFields?.find(f => f.Name === 'Hostname' && f.Value) ||
            !this.value()?.TransportFields?.find(f => f.Name === 'InFolder' && f.Value) ||
            !this.value()?.TransportFields?.find(f => f.Name === 'OutFolder' && f.Value)) {
          return ['Invalid'];
        }
        break;
      default:
        break;
    }
    return [];
  }


  /*
   * Validator interface methods
   */

  validate() {
    const frm = this.form()?.form;
    if (frm?.disabled ?? true) {
      return [];
    }
    return this.validateTransport();
  }


  /*
   * ControlValueAccessor interface methods
   */

  writeValue(obj: Transport): void {
    this.value.set(obj);
  }

  onChange: (value: unknown) => void = () => { };
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(!!isDisabled);
  }
}
